import { ReactElement, useCallback, useMemo } from 'react'
import { LoadingButton } from '@mui/lab'
import { Alert, Box, Button } from '@mui/material'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import type { AffectedAnchorDataDTO } from '@api/fastAPI.schemas'
import { useGetAllProjectShearConnections } from '@api/project-shear-connections/project-shear-connections'
import { DialogBase } from '@ui/feedback'
import { useSystemManagerStore } from '@editorStores'
import { useElementLabel } from '@editorHooks'
import LostAnchorDataToolbar from './components/LostAnchorDataToolbar'
import {
  buildLostAnchorDataColumns,
  buildLostAnchorDataRows,
  buildLostShearAnchorRows,
} from './utils'

interface Props {
  projectGuid: string
  affectedAnchorData: AffectedAnchorDataDTO | null
  isSaving: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Asks before saving a stiffening configuration that drops manual loads, comments or
 * user-defined Zuganker, or Schubverbinder picked by hand: those are stored per anchor or segment,
 * and the changed walls (and the walls above them) get new anchors and segments.
 */
const LostAnchorDataDialog = ({
  projectGuid,
  affectedAnchorData,
  isSaving,
  onConfirm,
  onCancel,
}: Props): ReactElement => {
  const elementLabel = useElementLabel()
  const connections = useSystemManagerStore(state => state.connections)
  const { data: shearConnections } = useGetAllProjectShearConnections(projectGuid, {
    query: { enabled: !!affectedAnchorData?.shearAnchors.length },
  })

  const connectorLabel = useCallback(
    (guid: string) => {
      const connection = connections.find(candidate => candidate.guid === guid)
      return connection ? `${connection.short_name} – ${connection.name}` : guid
    },
    [connections],
  )

  const shearConnectorLabel = useCallback(
    (guid: string) => {
      const connection = shearConnections?.find(candidate => candidate.guid === guid)
      return connection ? `${connection.short_name} – ${connection.name}` : guid
    },
    [shearConnections],
  )

  const rows = useMemo(
    () => [
      ...buildLostAnchorDataRows(affectedAnchorData?.anchors ?? [], elementLabel, connectorLabel),
      ...buildLostShearAnchorRows(
        affectedAnchorData?.shearAnchors ?? [],
        elementLabel,
        shearConnectorLabel,
      ),
    ],
    [affectedAnchorData, elementLabel, connectorLabel, shearConnectorLabel],
  )
  const columns = useMemo(
    () => buildLostAnchorDataColumns(affectedAnchorData?.anchors ?? []),
    [affectedAnchorData],
  )

  return (
    <DialogBase
      open={!!affectedAnchorData}
      onClose={isSaving ? () => undefined : onCancel}
      title="Eingaben an Ankern gehen verloren"
      dialogProps={{ maxWidth: 'lg', fullWidth: true }}
    >
      <Box data-cy="lost-anchor-data-dialog">
        <Alert severity="warning" sx={{ mb: 2 }}>
          Durch die geänderte Konfiguration werden die Zuganker der betroffenen Wände und der Wände
          darüber sowie die Wandabschnitte der betroffenen Wände neu bestimmt. Die folgenden
          manuellen Lasten, Kommentare, festgelegten Zuganker und festgelegten Schubverbinder werden
          dabei gelöscht. Laden Sie die Liste bei Bedarf herunter, um die Werte anschließend erneut
          einzutragen.
        </Alert>
        <Box height={400}>
          <DataGridPremium
            rows={rows}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            hideFooter
            slots={{ toolbar: LostAnchorDataToolbar }}
            data-cy="lost-anchor-data-grid"
          />
        </Box>
      </Box>
      <>
        <Button
          size="small"
          onClick={onCancel}
          disabled={isSaving}
          data-cy="lost-anchor-data-cancel"
        >
          Abbrechen
        </Button>
        <LoadingButton
          size="small"
          variant="contained"
          onClick={onConfirm}
          loading={isSaving}
          data-cy="lost-anchor-data-confirm"
        >
          Trotzdem speichern
        </LoadingButton>
      </>
    </DialogBase>
  )
}

export default LostAnchorDataDialog
